import { useEffect, useRef } from 'react';

interface MultiTapOptions {
  /** How many quick taps trigger the action. Default 3 (triple-tap). */
  taps?: number;
  /** Max gap between consecutive taps, ms. Default 450. */
  withinMs?: number;
  /** Disable the listener entirely (e.g. desktop). Default true. */
  enabled?: boolean;
}

/**
 * Fire `onTrigger` on a quick N-tap anywhere on the page (Alex 2026-06-20:
 * a thumb-friendly way to pop the mobile agent-switcher while driving,
 * instead of hunting for the back-arrow).
 *
 * Deliberately NON-invasive:
 *  - A "tap" is a short (<250ms), still (<12px) pointerdown→up. Long-press
 *    (text selection) and scroll/drag are NOT taps, so they're untouched.
 *  - Taps on inputs/buttons/links/contenteditable are ignored AND reset the
 *    counter — typing, sending, and native triple-tap-to-select-line keep
 *    working; the gesture only arms over passive areas (the conversation).
 *  - Capture phase so children that stopPropagation can't swallow it.
 */
export function useMultiTap(onTrigger: () => void, opts: MultiTapOptions = {}) {
  const { taps = 3, withinMs = 450, enabled = true } = opts;
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    if (!enabled) return;

    let count = 0;
    let lastTapTs = 0;
    let downX = 0;
    let downY = 0;
    let downT = 0;

    const onDown = (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
      downT = e.timeStamp;
    };

    const onUp = (e: PointerEvent) => {
      const duration = e.timeStamp - downT;
      const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
      // Not a clean tap (long-press / scroll / drag) → break the sequence.
      if (duration > 250 || moved > 12) {
        count = 0;
        return;
      }
      // Interactive / editable target → leave it to the OS and reset, so we
      // never hijack typing, button mashing, or text-selection gestures.
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, button, a, [contenteditable="true"], [role="button"]')) {
        count = 0;
        return;
      }
      const now = e.timeStamp;
      count = now - lastTapTs <= withinMs ? count + 1 : 1;
      lastTapTs = now;
      if (count >= taps) {
        count = 0;
        onTriggerRef.current();
      }
    };

    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
    };
  }, [taps, withinMs, enabled]);
}

interface TapGestureHandlers {
  double?: () => void;
  triple?: () => void;
}

/**
 * Distinguish a double- from a triple-tap (Alex 2026-06-20: double = agent
 * quickview, triple = group chat). Same non-invasive tap detection as
 * useMultiTap, but it WAITS `withinMs` after the last tap before deciding —
 * so a third tap can still arrive and upgrade a double into a triple.
 */
export function useTapGesture(handlers: TapGestureHandlers, opts: { withinMs?: number; enabled?: boolean } = {}) {
  const { withinMs = 300, enabled = true } = opts;
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!enabled) return;

    let count = 0;
    let settle: ReturnType<typeof setTimeout> | null = null;
    let downX = 0, downY = 0, downT = 0;

    const reset = () => { count = 0; if (settle) { clearTimeout(settle); settle = null; } };

    const onDown = (e: PointerEvent) => { downX = e.clientX; downY = e.clientY; downT = e.timeStamp; };
    const onUp = (e: PointerEvent) => {
      const duration = e.timeStamp - downT;
      const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
      if (duration > 250 || moved > 12) { reset(); return; }
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, button, a, [contenteditable="true"], [role="button"]')) {
        reset();
        return;
      }
      count += 1;
      if (settle) clearTimeout(settle);
      // Wait a beat after the last tap, then act on the final count — lets a
      // 3rd tap upgrade a double into a triple instead of firing both.
      settle = setTimeout(() => {
        if (count === 2) ref.current.double?.();
        else if (count >= 3) ref.current.triple?.();
        count = 0;
        settle = null;
      }, withinMs);
    };

    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
      if (settle) clearTimeout(settle);
    };
  }, [withinMs, enabled]);
}
